import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';
import { formatCurrencyFromCents } from '@/utils/currency';

type ParentBooking = Awaited<ReturnType<typeof clubsBookingApi.listParentBookings>>[number];
type BookingClubMeta = {
  logoUrl?: string;
  clubName?: string;
};
type BookingChildView = {
  key: string;
  index: number;
  id?: string;
  name: string;
  dateOfBirth?: string;
};

const formatSessionDateTime = (startDateTime?: string, endDateTime?: string) => {
  if (!startDateTime || !endDateTime) return 'Time unavailable';
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);
  return `${start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${start.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })} - ${end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const getBookingChildren = (booking: ParentBooking): BookingChildView[] => {
  const children = Array.isArray(booking.children)
    ? booking.children
        .map((child, index) => {
          const firstName = String(child?.firstName || '').trim();
          const lastName = String(child?.lastName || '').trim();
          const name = [firstName, lastName].filter(Boolean).join(' ').trim();
          const dateOfBirth = String(child?.dateOfBirth || '').trim() || undefined;
          const id = String(child?.id || '').trim() || undefined;
          if (!name) return null;
          const key = id || `${name.toLowerCase()}::${dateOfBirth || ''}`;
          return { key, index, id, name, dateOfBirth };
        })
        .filter(Boolean) as BookingChildView[]
    : [];
  return children;
};

const getCartItemChildIndexes = (item: { childIndexes?: number[] }, childCount: number) => {
  const validIndexes = (item.childIndexes || [])
    .map((childIndex) => Number(childIndex))
    .filter((childIndex) => Number.isInteger(childIndex) && childIndex >= 0 && childIndex < childCount);
  return validIndexes.length ? Array.from(new Set(validIndexes)) : Array.from({ length: childCount }, (_, index) => index);
};

const getSessionIdsForChild = (booking: ParentBooking, childIndex: number) => {
  const childCount = Math.max(1, booking.children?.length || booking.quantity || 1);
  const cartItems = booking.cartItems?.length
    ? booking.cartItems
    : [{ sessionIds: booking.sessionIds || [], childIndexes: [] }];
  const sessionIds = new Set<string>();

  cartItems.forEach((item) => {
    if (!getCartItemChildIndexes(item, childCount).includes(childIndex)) return;
    (item.sessionIds || booking.sessionIds || []).forEach((sessionId) => sessionIds.add(String(sessionId)));
  });

  return Array.from(sessionIds);
};

const ParentChildrenPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState<ParentBooking[]>([]);
  const [selectedChildKey, setSelectedChildKey] = useState('all');
  const [clubMetaByEventId, setClubMetaByEventId] = useState<Record<string, BookingClubMeta>>({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const token = auth.getToken();
        if (!token) {
          setError('Your parent session has expired. Please log in again.');
          return;
        }
        clubsBookingApi.setAuthToken(token);
        const data = await clubsBookingApi.listParentBookings();
        setItems(data || []);

        const eventIds = Array.from(new Set((data || []).map((booking) => booking.event?.id).filter(Boolean))) as string[];
        const entries = await Promise.all(
          eventIds.map(async (eventId) => {
            try {
              const event = await clubsBookingApi.getPublicEvent(eventId);
              return [
                eventId,
                {
                  logoUrl: event?.clubProfile?.logoUrl,
                  clubName: event?.clubProfile?.clubName || '',
                },
              ] as const;
            } catch {
              return [eventId, { logoUrl: undefined, clubName: '' }] as const;
            }
          }),
        );
        setClubMetaByEventId(Object.fromEntries(entries));
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          auth.clearAuth();
          clubsBookingApi.clearAuthToken();
          window.location.assign('/parent/login');
          return;
        }
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load child sessions');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const childOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string }>();
    items.forEach((booking) => {
      getBookingChildren(booking).forEach((child) => {
        if (!map.has(child.key)) {
          map.set(child.key, { key: child.key, label: child.name });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [items]);

  const rows = useMemo(() => {
    return items.flatMap((booking) => {
      const eventSessions = booking.event?.sessions || [];
      return getBookingChildren(booking).map((child) => ({
        booking,
        child,
        displaySessions: getSessionIdsForChild(booking, child.index)
          .map((sessionId) => eventSessions.find((session) => session.id === sessionId))
          .filter(Boolean) as Array<{ id: string; startDateTime: string; endDateTime: string }>,
      }));
    });
  }, [items]);

  const filteredRows = useMemo(
    () => (selectedChildKey === 'all' ? rows : rows.filter((entry) => entry.child.key === selectedChildKey)),
    [rows, selectedChildKey],
  );

  return (
    <ParentPortalShell title="My children" subtitle="See activities and session times for each child">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        {loading && <p className="text-sm text-slate-500">Loading child sessions...</p>}
        {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {!loading && !error && (
          <>
            <div className="mb-4 overflow-x-auto pb-1">
              <div className="inline-flex min-w-full gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedChildKey('all')}
                  className={`whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-medium ${
                    selectedChildKey === 'all'
                      ? 'border-[#1F8F5F] bg-[#F3FBF7] text-[#1F8F5F]'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  All children
                </button>
                {childOptions.map((child) => (
                  <button
                    key={child.key}
                    type="button"
                    onClick={() => setSelectedChildKey(child.key)}
                    className={`whitespace-nowrap rounded-lg border px-3 py-2 text-sm font-medium ${
                      selectedChildKey === child.key
                        ? 'border-[#1F8F5F] bg-[#F3FBF7] text-[#1F8F5F]'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {child.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredRows.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                <p className="text-sm text-slate-600">No sessions found for this child.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRows.map(({ booking, child, displaySessions }) => {
                  const eventId = booking.event?.id || '';
                  const clubMeta = eventId ? clubMetaByEventId[eventId] : undefined;
                  const clubName = clubMeta?.clubName || booking.event?.title || 'Club';
                  const clubInitials = clubName
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((part) => part.charAt(0).toUpperCase())
                    .join('') || 'SM';

                  return (
                    <article key={`${booking._id}-${child.key}`} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-3">
                            {clubMeta?.logoUrl ? (
                              <img src={clubMeta.logoUrl} alt={clubName} className="h-9 w-9 shrink-0 rounded-md object-contain" />
                            ) : (
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#F3FBF7] text-xs font-semibold text-[#1F8F5F]">
                                {clubInitials}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="truncate text-base font-semibold text-slate-900">{booking.event?.title || 'Activity'}</p>
                              <p className="truncate text-xs text-slate-600">{booking.event?.location || 'Location unavailable'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-right">
                          <p className="text-xs text-emerald-700">Booking total</p>
                          <p className="text-sm font-semibold text-emerald-900">
                            {formatCurrencyFromCents(booking.totalPriceCents, booking.currency || 'GBP')}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <p className="text-xs text-slate-500">Child</p>
                        <p className="text-sm font-medium text-slate-900">{child.name}</p>
                        {child.dateOfBirth && (
                          <p className="text-xs text-slate-500">DOB: {new Date(child.dateOfBirth).toLocaleDateString('en-GB')}</p>
                        )}
                      </div>

                      <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <p className="text-xs font-medium text-slate-500">Sessions</p>
                        <ul className="mt-1 space-y-1 text-sm text-slate-700">
                          {displaySessions.map((session) => (
                            <li key={`${booking._id}-${child.key}-${session.id}`}>
                              {formatSessionDateTime(session.startDateTime, session.endDateTime)}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {booking.event?.id && (
                        <div className="mt-3">
                          <Link to={`/book/${booking.event.id}`} className="text-sm font-medium text-[#1F8F5F] hover:underline">
                            View activity page
                          </Link>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
    </ParentPortalShell>
  );
};

export default ParentChildrenPage;
